import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  message,
  Tabs,
  Card as AntdCard,
  Tooltip,
  Empty,
} from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PlayCircleOutlined,
  StopOutlined,
  SearchOutlined,
  ReloadOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  UserOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { adminService, LicenseUsage, FilterLicenseUsagesDto } from '../services/admin';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const LicenseUsageList: React.FC = () => {
  const [activeTab, setActiveTab] = useState('usages');
  const [filters, setFilters] = useState<FilterLicenseUsagesDto>({
    page: 1,
    limit: 20,
  });
  const [fileNameByFileId, setFileNameByFileId] = useState<Record<string, string | null>>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
  });

  // Fetch usage statistics
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['license-usage-stats'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('认证失败，请重新登录');
        return null;
      }
      return adminService.getLicenseUsageStats();
    },
  });

  // Fetch license usages with filters
  const { data: usages, refetch, isLoading } = useQuery({
    queryKey: ['license-usages', filters],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('认证失败，请重新登录');
        return { data: [], total: 0, page: 1, limit: 20, totalPages: 1 };
      }

      try {
        return await adminService.getLicenseUsages(filters);
      } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 401) {
          message.error('认证失败，请重新登录');
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else {
          message.error(`数据加载失败: ${(axiosError.response?.data as any)?.message || axiosError.message}`);
        }
        throw error;
      }
    },
  });

  // Fetch anomalies
  const { data: anomalies, refetch: refetchAnomalies } = useQuery({
    queryKey: ['license-usage-anomalies'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('认证失败，请重新登录');
        return [];
      }
      return adminService.getLicenseUsageAnomalies();
    },
  });

  useEffect(() => {
    const knownFileIds = new Set(Object.keys(fileNameByFileId));
    const usageFileIds = (usages?.data || []).map((u) => u.fileId).filter(Boolean);
    const anomalyFileIds = (anomalies || []).map((a) => a.fileId).filter(Boolean);
    const uniqueFileIds = Array.from(new Set([...usageFileIds, ...anomalyFileIds]));

    const missingFileIds = uniqueFileIds.filter((fileId) => !knownFileIds.has(fileId));
    if (missingFileIds.length === 0) return;

    let cancelled = false;

    const fetchBatch = async (batch: string[]) => {
      return Promise.all(
        batch.map(async (fileId) => {
          try {
            const content = await adminService.getContentByFileId(fileId);
            return { fileId, filename: content?.filename ?? null };
          } catch {
            return { fileId, filename: null };
          }
        })
      );
    };

    (async () => {
      const batchSize = 10;
      const updates: Record<string, string | null> = {};
      for (let i = 0; i < missingFileIds.length; i += batchSize) {
        // eslint-disable-next-line no-await-in-loop
        const results = await fetchBatch(missingFileIds.slice(i, i + batchSize));
        for (const r of results) updates[r.fileId] = r.filename;
      }
      if (cancelled) return;
      setFileNameByFileId((prev) => ({ ...prev, ...updates }));
    })();

    return () => {
      cancelled = true;
    };
  }, [usages?.data, anomalies, fileNameByFileId]);

  const getActionTag = (action: string) => {
    const colors: Record<string, string> = {
      'play': 'green',
      'pause': 'orange',
      'stop': 'red',
      'seek': 'blue',
      'seek_forward': 'cyan',
      'seek_backward': 'purple',
    };

    return (
      <Tag color={colors[action] || 'default'}>
        {action?.toUpperCase() || 'UNKNOWN'}
      </Tag>
    );
  };

  const getContentTypeTag = (contentType: string) => {
    const colors: Record<string, string> = {
      'video': 'blue',
      'audio': 'green',
      'document': 'orange',
    };

    return (
      <Tag color={colors[contentType] || 'gray'}>
        {contentType?.toUpperCase() || 'UNKNOWN'}
      </Tag>
    );
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
    setFilters({
      ...filters,
      page: pagination.current,
      limit: pagination.pageSize,
    });
  };

  const handleFilterApply = () => {
    const newFilters = { ...filters, page: 1 };
    setFilters(newFilters);
    setPagination({ ...pagination, current: 1 });
    // Clear any existing error states
    setTimeout(() => refetch(), 100);
  };

  const handleFilterReset = () => {
    const resetFilters = { page: 1, limit: 20 };
    setFilters(resetFilters);
    setPagination({ ...pagination, current: 1, pageSize: 20 });
    // Clear any existing error states
    setTimeout(() => refetch(), 100);
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (time: string) => (
        <div>
          <div>{dayjs(time).format('YYYY-MM-DD HH:mm')}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {dayjs(time).fromNow()}
          </div>
        </div>
      ),
    },
    {
      title: 'User',
      key: 'user',
      width: 150,
      render: (_: string, record: LicenseUsage) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.user?.username || 'Unknown'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.user?.email || '-'}</div>
        </div>
      ),
    },
    {
      title: 'File ID',
      dataIndex: 'fileId',
      key: 'fileId',
      width: 120,
      ellipsis: true,
      render: (fileId: string) => (
        <Tooltip title={fileId}>
          <span>{fileId.substring(0, 8)}...</span>
        </Tooltip>
      ),
    },
    {
      title: 'File Name',
      key: 'filename',
      width: 220,
      ellipsis: true,
      render: (_: string, record: LicenseUsage) => {
        const filename = fileNameByFileId[record.fileId];
        if (typeof filename === 'string' && filename.length > 0) {
          return <Tooltip title={filename}>{filename}</Tooltip>;
        }
        return <span style={{ color: '#999' }}>-</span>;
      },
    },
    {
      title: 'Content Type',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 120,
      render: (contentType: string) => getContentTypeTag(contentType),
    },
    {
      title: 'Device',
      dataIndex: 'deviceId',
      key: 'deviceId',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action: string) => getActionTag(action),
    },
    {
      title: 'Play Duration',
      dataIndex: 'playDuration',
      key: 'playDuration',
      width: 120,
      render: (duration: number) => formatDuration(duration),
    },
    {
      title: 'Total Duration',
      dataIndex: 'totalDuration',
      key: 'totalDuration',
      width: 120,
      render: (duration: number) => formatDuration(duration),
    },
    {
      title: 'IP/Location',
      key: 'location',
      width: 150,
      render: (_: string, record: LicenseUsage) => (
        <div>
          {record.accessIP && <div style={{ fontSize: '12px' }}>🌐 {record.accessIP}</div>}
          {record.accessLocation && <div style={{ fontSize: '12px', color: '#666' }}>📍 {record.accessLocation}</div>}
        </div>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 100,
      render: (_: string, record: LicenseUsage) => {
        if (!record.totalDuration) return null;
        const progress = Math.min((record.playDuration / record.totalDuration) * 100, 100);
        return (
          <div>
            <div style={{ fontSize: '11px', marginBottom: '4px' }}>
              {progress.toFixed(1)}%
            </div>
            <div style={{
              width: '60px',
              height: '4px',
              backgroundColor: '#f0f0f0',
              borderRadius: '2px'
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: progress > 90 ? '#52c41a' : progress > 50 ? '#1890ff' : '#ffbb96',
                borderRadius: '2px',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        );
      },
    }
  ];

  return (
    <div>
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="📋 Usage Records" key="usages">
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Usages"
                    value={stats?.totalUsages || 0}
                    prefix={<LineChartOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Today's Usages"
                    value={stats?.todayUsages || 0}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Unique Users"
                    value={stats?.uniqueUsers || 0}
                    prefix={<UserOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => { refetch(); refetchStats(); }}
                    style={{ width: '100%', marginBottom: 8 }}
                  >
                    Refresh Data
                  </Button>
                  {stats?.contentTypeDistribution.map(item => (
                    <div key={item.contentType} style={{ fontSize: '12px', marginBottom: 4 }}>
                      <span style={{ color: '#666' }}>{item.contentType}:</span> {item.count}
                    </div>
                  ))}
                </Card>
              </Col>
            </Row>

            <Card>
              <div style={{ marginBottom: 16 }}>
                <Input.Group compact style={{ marginBottom: 16 }}>
                  <Input
                    placeholder="File ID"
                    onChange={(e) => setFilters({ ...filters, fileId: e.target.value })}
                    style={{ width: 180 }}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                  <Input
                    placeholder="Device ID"
                    onChange={(e) => setFilters({ ...filters, deviceId: e.target.value })}
                    style={{ width: 150 }}
                    allowClear
                  />
                  <Select
                    placeholder="Content Type"
                    onChange={(value) => setFilters({ ...filters, contentType: value })}
                    style={{ width: 120 }}
                    allowClear
                  >
                    <Option value="video">Video</Option>
                    <Option value="audio">Audio</Option>
                    <Option value="document">Document</Option>
                  </Select>
                  <Select
                    placeholder="Action"
                    onChange={(value) => setFilters({ ...filters, action: value })}
                    style={{ width: 100 }}
                    allowClear
                  >
                    <Option value="play">Play</Option>
                    <Option value="pause">Pause</Option>
                    <Option value="stop">Stop</Option>
                    <Option value="seek">Seek</Option>
                  </Select>
                </Input.Group>

                <RangePicker
                  style={{ marginRight: 8, marginBottom: 8 }}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  onChange={(dates) => {
                    if (dates) {
                      setFilters({
                        ...filters,
                        startDate: dates[0]?.format('YYYY-MM-DDTHH:mm:ss'),
                        endDate: dates[1]?.format('YYYY-MM-DDTHH:mm:ss'),
                      });
                    } else {
                      setFilters({
                        ...filters,
                        startDate: undefined,
                        endDate: undefined,
                      });
                    }
                  }}
                />

                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleFilterApply}
                  style={{ marginRight: 8 }}
                >
                  Search
                </Button>
                <Button
                  onClick={handleFilterReset}
                >
                  Reset
                </Button>
              </div>

              <Table
                dataSource={usages?.data || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: usages?.total || 0,
                  showSizeChanger: pagination.showSizeChanger,
                }}
                onChange={handleTableChange}
                size="small"
                scroll={{ x: 1400 }}
              />
            </Card>
          </TabPane>

          <TabPane tab="⚠️ Anomalies" key="anomalies">
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Detected Anomalies"
                    value={anomalies?.length || 0}
                    valueStyle={{ color: '#cf1322' }}
                    prefix={<WarningOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => refetchAnomalies()}
                    style={{ width: '100%' }}
                  >
                    Check Anomalies
                  </Button>
                </Card>
              </Col>
            </Row>

            <Card title="Unusual Usage Patterns">
              {(!anomalies || anomalies.length === 0) ? (
                <Empty
                  description="No unusual usage patterns detected in the last 24 hours"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Table
                  dataSource={anomalies}
                  columns={[
                    {
                      title: 'User',
                      dataIndex: 'userId',
                      key: 'userId',
                      width: 80,
                    },
                    {
                      title: 'File ID',
                      dataIndex: 'fileId',
                      key: 'fileId',
                      ellipsis: true,
                      render: (fileId: string) => (
                        <Tooltip title={fileId}>
                          <span>{fileId.substring(0, 12)}...</span>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'File Name',
                      key: 'filename',
                      ellipsis: true,
                      render: (_: unknown, record: { fileId: string }) => {
                        const filename = fileNameByFileId[record.fileId];
                        if (typeof filename === 'string' && filename.length > 0) {
                          return <Tooltip title={filename}>{filename}</Tooltip>;
                        }
                        return <span style={{ color: '#999' }}>-</span>;
                      },
                    },
                    {
                      title: 'Anomaly Type',
                      dataIndex: 'anomalyType',
                      key: 'anomalyType',
                      render: (type: string) => (
                        <Tag color={type === 'high_play_count' ? 'red' : 'orange'}>
                          {type === 'high_play_count' ? 'High Play Count' : 'Excessive Duration'}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Play Count',
                      dataIndex: 'playCount',
                      key: 'playCount',
                      render: (count: number) => <span style={{ fontWeight: 600, color: '#cf1322' }}>{count}</span>,
                    },
                    {
                      title: 'Total Duration',
                      dataIndex: 'totalPlayDuration',
                      key: 'totalPlayDuration',
                      render: (duration: number) => (
                        <div>
                          <div>{formatDuration(duration)}</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            Avg: {Math.round(duration / (anomalies?.find(a =>
                              a.totalPlayDuration === duration
                            )?.playCount || 1))}s/play
                          </div>
                        </div>
                      ),
                    },
                  ]}
                  rowKey={(record) => `${record.userId}-${record.fileId}`}
                  pagination={{
                    pageSize: 10,
                    showQuickJumper: true,
                    showTotal: (total) => `${total} anomalies detected`,
                  }}
                  size="small"
                />
              )}
            </Card>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default LicenseUsageList;
